import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Video, MapPin, X, Clock } from "lucide-react";
import api, { drName } from "../lib/api";
import { FullScreenLoader, EmptyState } from "../components/ui-kit";

const scopes = [
  { key: "upcoming", label: "Upcoming" },
  { key: "past", label: "Past" },
  { key: "all", label: "All" },
];

const AppointmentHistory = () => {
  const nav = useNavigate();
  const [scope, setScope] = useState("upcoming");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/patient/appointments", { params: { scope } });
      setItems(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = async (id) => {
    if (!window.confirm("Cancel this appointment?")) return;
    await api.delete(`/patient/appointments/${id}`);
    load();
  };

  return (
    <div className="pb-24">
      <div className="px-5 pt-6 pb-3 flex items-center gap-3">
        <button data-testid="history-back" onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white flex items-center justify-center border border-[#0A4C36]/10">
          <ChevronLeft size={20} className="text-[#0A4C36]" />
        </button>
        <div>
          <h1 className="font-instrument text-2xl font-semibold text-[#0A4C36] leading-tight">Appointments</h1>
          <p className="text-xs text-[#78716C]">Your complete history</p>
        </div>
      </div>

      <div className="px-5">
        <div className="flex gap-2 p-1 bg-white rounded-full border border-[#0A4C36]/5">
          {scopes.map((s) => (
            <button
              key={s.key}
              data-testid={`scope-${s.key}`}
              onClick={() => setScope(s.key)}
              className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${
                scope === s.key ? "bg-[#0A4C36] text-white" : "text-[#57534E]"
              }`}
            >{s.label}</button>
          ))}
        </div>
      </div>

      <div className="px-5 mt-4 space-y-3">
        {loading ? <FullScreenLoader /> : items.length === 0 ? (
          <EmptyState title="No appointments" subtitle="Book one from the dashboard" />
        ) : items.map((a) => (
          <div key={a.id} data-testid={`history-card-${a.id}`} className="card">
            <div className="flex items-center justify-between mb-2">
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                a.mode === "Remote" ? "bg-[#E6F0EC] text-[#0A4C36]" : "bg-[#FCD9C8] text-[#F4844B]"
              }`}>
                {a.mode === "Remote" ? <Video size={12} /> : <MapPin size={12} />}
                {a.mode}
              </span>
              <span className="text-xs text-[#78716C] flex items-center gap-1"><Clock size={12} />{a.date} · {a.time}</span>
            </div>
            <div className="font-instrument text-lg font-semibold text-[#1C1917]">{drName(a.doctor_name)}</div>
            <div className="text-xs text-[#78716C]">{Array.isArray(a.specialties) ? a.specialties.join(", ") : (a.specialty || "")}</div>
            {a.notes && <div className="mt-2 text-xs text-[#57534E] italic">&ldquo;{a.notes}&rdquo;</div>}
            <div className="mt-3 flex gap-2">
              {a.mode === "Remote" && a.meeting_url && (
                <Link to={`/meeting?url=${encodeURIComponent(a.meeting_url)}`} data-testid={`history-join-${a.id}`} className="flex-1 py-2.5 rounded-2xl bg-[#F4844B] text-white text-sm font-medium text-center no-underline">
                  Join meeting
                </Link>
              )}
              {(a.status || "").toLowerCase() !== "cancelled" && new Date(a.date) >= new Date(new Date().toDateString()) && (
                <button data-testid={`history-cancel-${a.id}`} onClick={() => cancel(a.id)} className="flex-1 py-2.5 rounded-2xl bg-red-50 text-red-500 text-sm font-medium flex items-center justify-center gap-1">
                  <X size={14} /> Cancel
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AppointmentHistory;
